import { createContext, useContext} from "react";
import UserProfile, { UserProfileSettings } from "../models/user/UserProfile"
import { useUpdateUserProfile, useUpdateUserProfileSettings, useUserProfile } from "../hooks/user";
import { useAuth } from "../auth";
import dayjs from "dayjs";

type UserProfileContextType = {
  userProfile: UserProfile | undefined;
  userProfileSettings: UserProfileSettings | undefined;
  dateFormat: string;
  formatDate: (date: string | Date) => string;

  updateProfile: ReturnType<typeof useUpdateUserProfile>["mutate"];
  refetchProfile: () => void;

  isLoadingProfile: boolean;
  isSavingProfile: boolean;
  profileError: Error | null;

  updateProfileSettings: ReturnType<typeof useUpdateUserProfileSettings>["mutate"];
  isSavingProfileSettings: boolean;
};

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

export const UserProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  // 🔵 React Query fetch — single source of truth
  const {
    data: profile,
    refetch,
    isLoading,
    isFetching,
    error
  } = useUserProfile({ enabled: isAuthenticated });

  // 🟢 React Query mutation for updates
  const { mutate: updateProfile, isPending: isSavingProfile } = useUpdateUserProfile();

  const { mutate: updateProfileSettings, isPending: isSavingProfileSettings } = useUpdateUserProfileSettings();

  // 🟡 Derived values (NOT stored, computed directly)
  const userProfileSettings = profile?.userProfileSettings;

  const dateFormat =
    userProfileSettings?.dateFormat ?? getLocaleDateFormat();

  const formatDate = (date: string | Date): string => dayjs(date).format(dateFormat);

  return (
    <UserProfileContext.Provider
      value={{
        userProfile: profile,
        userProfileSettings,
        dateFormat,
        formatDate,

        updateProfile,
        refetchProfile: refetch,

        isLoadingProfile: isLoading || isFetching,
        isSavingProfile,
        profileError: error,

        updateProfileSettings,
        isSavingProfileSettings
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfileContext = (): UserProfileContextType => {
  const ctx = useContext(UserProfileContext);
  if(!ctx) {
    throw new Error("useUserProfile must be used withing UserProfileProvider");
  }

  return ctx;
}

// Convert browser locale → date format
export function getLocaleDateFormat(): string {
  const userLocale = navigator.language || navigator.languages?.[0] || "en-US";

  const formatter = new Intl.DateTimeFormat(userLocale);
  const parts = formatter.formatToParts(new Date(2020, 11, 31)); // fixed sample date

  const map: Record<string, string> = {
    day: "DD",
    month: "MM",
    year: "YYYY",
  };

  return parts
    .map((p) => (map[p.type] ? map[p.type] : p.value))
    .join("");
}
